'use client';

import { useState } from 'react';
import { Share2, Facebook, Twitter, Mail, Link as LinkIcon, MessageCircle, Check } from 'lucide-react';

interface RecipeShareButtonsProps {
  title: string;
  slug: string;
  lang: string;
  description?: string;
}

export function RecipeShareButtons({ title, slug, lang, description }: RecipeShareButtonsProps) {
  const [copied, setCopied] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const shareUrl = typeof window !== 'undefined' 
    ? `${window.location.origin}/${lang}/blogs/eats/${slug}`
    : '';

  const shareText = description || title;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title,
          text: shareText,
          url: shareUrl,
        });
      } catch (err) {
        // User cancelled or error occurred
        console.log('Share cancelled');
      }
    } else {
      setIsExpanded(!isExpanded);
    }
  };

  const socialPlatforms = [
    {
      name: 'Facebook',
      icon: Facebook,
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      color: '#1877F2',
      bgGradient: 'linear-gradient(135deg, #1877F2 0%, #0C63D4 100%)',
    },
    {
      name: 'Twitter',
      icon: Twitter,
      url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(shareUrl)}`,
      color: '#1DA1F2',
      bgGradient: 'linear-gradient(135deg, #1DA1F2 0%, #0C8BD9 100%)',
    },
    {
      name: 'WhatsApp',
      icon: MessageCircle,
      url: `https://wa.me/?text=${encodeURIComponent(`${title} - ${shareUrl}`)}`,
      color: '#25D366',
      bgGradient: 'linear-gradient(135deg, #25D366 0%, #1EBE5D 100%)',
    },
    {
      name: 'Email',
      icon: Mail,
      url: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`Check out this recipe: ${shareUrl}`)}`,
      color: '#EA4335',
      bgGradient: 'linear-gradient(135deg, #EA4335 0%, #D33426 100%)',
    },
  ];

  return (
    <div style={{
      background: 'linear-gradient(135deg, #FFF5EB 0%, #FFFCF8 100%)',
      padding: '1.5rem',
      borderRadius: '20px',
      border: '2px solid #F8C8A0',
      boxShadow: '0 6px 20px rgba(248, 200, 160, 0.18)',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Decorative gradient orbs */}
      <div style={{
        position: 'absolute',
        top: '-50px',
        right: '-50px',
        width: '150px',
        height: '150px',
        background: 'radial-gradient(circle, rgba(248, 200, 160, 0.15) 0%, transparent 70%)',
        borderRadius: '50%',
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute',
        bottom: '-30px',
        left: '-30px',
        width: '120px',
        height: '120px',
        background: 'radial-gradient(circle, rgba(253, 185, 136, 0.12) 0%, transparent 70%)',
        borderRadius: '50%',
        pointerEvents: 'none',
      }} />

      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          marginBottom: '1rem',
          flexWrap: 'wrap',
          gap: '0.75rem',
        }}>
          <div>
            <h3 style={{
              fontSize: '1.125rem',
              fontWeight: 800,
              background: 'linear-gradient(135deg, #D97706 0%, #F59E0B 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              marginBottom: '0.25rem',
            }}>
              Love this recipe?
            </h3>
            <p style={{
              fontSize: '0.8125rem',
              color: '#92400E',
              margin: 0,
              fontWeight: 500,
            }}>
              Share it with other parents!
            </p>
          </div>

          {/* Main Share Button (Native or Toggle) */}
          <button
            onClick={handleNativeShare}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.625rem 1.25rem',
              background: 'linear-gradient(135deg, #D97706 0%, #F59E0B 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '100px',
              fontSize: '0.875rem',
              fontWeight: 700,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 3px 12px rgba(217, 119, 6, 0.3)',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px) scale(1.03)';
              e.currentTarget.style.boxShadow = '0 6px 18px rgba(217, 119, 6, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0) scale(1)';
              e.currentTarget.style.boxShadow = '0 3px 12px rgba(217, 119, 6, 0.3)';
            }}
          >
            <Share2 size={16} strokeWidth={2.5} />
            <span>Share Recipe</span>
          </button>
        </div>

        {/* Social Media Buttons Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))',
          gap: '0.75rem',
          marginBottom: '1rem',
        }}>
          {socialPlatforms.map((platform) => (
            <a
              key={platform.name}
              href={platform.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Share on ${platform.name}`}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.875rem 0.5rem',
                background: 'white',
                borderRadius: '12px',
                border: '2px solid #F8C8A0',
                textDecoration: 'none',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 6px rgba(248, 200, 160, 0.12)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-3px) scale(1.03)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(248, 200, 160, 0.25)';
                e.currentTarget.style.borderColor = platform.color;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                e.currentTarget.style.boxShadow = '0 2px 6px rgba(248, 200, 160, 0.12)';
                e.currentTarget.style.borderColor = '#F8C8A0';
              }}
            >
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: platform.bgGradient,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: `0 3px 10px ${platform.color}25`,
              }}>
                <platform.icon size={20} color="white" strokeWidth={2} />
              </div>
              <span style={{
                fontSize: '0.75rem',
                fontWeight: 700,
                color: '#92400E',
              }}>
                {platform.name}
              </span>
            </a>
          ))}
        </div>

        {/* Copy Link Button */}
        <button
          onClick={handleCopyLink}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            padding: '0.75rem',
            background: copied 
              ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)'
              : 'linear-gradient(135deg, rgba(217, 119, 6, 0.1) 0%, rgba(245, 158, 11, 0.1) 100%)',
            border: `2px solid ${copied ? '#10B981' : '#F8C8A0'}`,
            borderRadius: '12px',
            fontSize: '0.875rem',
            fontWeight: 700,
            color: copied ? 'white' : '#D97706',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            boxShadow: copied 
              ? '0 3px 12px rgba(16, 185, 129, 0.25)'
              : '0 2px 6px rgba(248, 200, 160, 0.15)',
          }}
          onMouseEnter={(e) => {
            if (!copied) {
              e.currentTarget.style.background = 'linear-gradient(135deg, rgba(217, 119, 6, 0.15) 0%, rgba(245, 158, 11, 0.15) 100%)';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 3px 10px rgba(248, 200, 160, 0.25)';
            }
          }}
          onMouseLeave={(e) => {
            if (!copied) {
              e.currentTarget.style.background = 'linear-gradient(135deg, rgba(217, 119, 6, 0.1) 0%, rgba(245, 158, 11, 0.1) 100%)';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 6px rgba(248, 200, 160, 0.15)';
            }
          }}
        >
          {copied ? (
            <>
              <Check size={16} strokeWidth={2.5} />
              <span>Link Copied!</span>
            </>
          ) : (
            <>
              <LinkIcon size={16} strokeWidth={2.5} />
              <span>Copy Recipe Link</span>
            </>
          )}
        </button>
      </div>

      {/* Mobile responsive styles */}
      <style jsx>{`
        @media (max-width: 640px) {
          div[style*="gridTemplateColumns"] {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
      `}</style>
    </div>
  );
}

